import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Building2, Building, Compass, ExternalLink, Layers, CheckCircle2, FileText } from "lucide-react";
import Header from "@/components/Header";
import AhjIntelCard from "@/components/AhjIntelCard";
import AhjPlaybook from "@/components/AhjPlaybook";
import AhjContactsBadge from "@/components/AhjContactsBadge";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

const TYPE_BADGE = {
  city: "bg-blue-50 text-blue-700 border-blue-200",
  town: "bg-emerald-50 text-emerald-700 border-emerald-200",
  village: "bg-violet-50 text-violet-700 border-violet-200",
  township: "bg-amber-50 text-amber-700 border-amber-200",
  borough: "bg-rose-50 text-rose-700 border-rose-200",
  CDP: "bg-slate-100 text-slate-700 border-slate-200",
  municipality: "bg-indigo-50 text-indigo-700 border-indigo-200",
};

export default function PlaceDetail() {
  const { geoid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get(`/places/${geoid}`)
      .then((r) => {
        setData(r.data);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.response?.data?.detail || "Place not found");
        setLoading(false);
      });
  }, [geoid]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Header />
        <div className="max-w-6xl mx-auto px-6 py-12 space-y-6">
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-12 w-2/3" />
          <div className="grid grid-cols-3 gap-4">
            {[1,2,3].map((i) => <Skeleton key={i} className="h-24" />)}
          </div>
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="place-error">
          <h2 className="font-outfit text-3xl font-semibold text-slate-900 mb-3">Place not found</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button onClick={() => navigate("/map")} className="bg-slate-900 hover:bg-slate-800 text-white">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to map
          </Button>
        </div>
      </div>
    );
  }

  const { place, parent_county, county_permits, city_permits, has_city_authority, city_intel, county_intel } = data;
  const typeBadge = TYPE_BADGE[place.type] || "bg-slate-100 text-slate-700 border-slate-200";

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="place-detail-page">
      <Header />
      <main className="max-w-6xl mx-auto px-6 py-12">
        <Link to="/map" data-testid="place-back-link" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to map
        </Link>

        <div className="mb-10">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-emerald-700 mb-3 flex items-center gap-1.5">
            <MapPin className="w-3 h-3" /> {place.state_name}
          </div>
          <h1 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight text-slate-900" data-testid="place-detail-name">
            {place.full_name}
          </h1>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Badge className={`${typeBadge} font-mono capitalize`}>{place.type}</Badge>
            {place.lsad_label && <Badge variant="outline" className="font-mono">{place.lsad_label}</Badge>}
            <Badge variant="outline" className="font-mono">{place.state_abbr}</Badge>
            <Badge variant="outline" className="font-mono">GEOID {place.geoid}</Badge>
            <AhjContactsBadge ahjKey={place.geoid} scope="city" />
            {parent_county && (
              <AhjContactsBadge ahjKey={parent_county.fips} scope="county" />
            )}
          </div>
        </div>

        {/* Dual-jurisdiction callout */}
        <div className="mb-10 rounded-xl border border-emerald-200 bg-emerald-50/60 p-6 flex flex-wrap items-center gap-6" data-testid="dual-jurisdiction-banner">
          <div className="flex items-center gap-3 flex-1 min-w-[260px]">
            <Layers className="w-5 h-5 text-emerald-700" />
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-emerald-700 mb-1">
                {has_city_authority ? "Two-layer permitting" : "County-only permitting"}
              </div>
              <div className="text-sm text-slate-800 leading-relaxed">
                {has_city_authority ? (
                  <>Permits in <strong>{place.name}</strong> typically require approvals from both the <strong>{place.lsad_label || place.type}</strong> and {parent_county ? <strong>{parent_county.full_name}</strong> : "the parent jurisdiction"}.</>
                ) : (
                  <>{place.full_name} typically defers to {parent_county ? <strong>{parent_county.full_name}</strong> : "the county"} for permitting. Apply directly through the county.</>
                )}
              </div>
            </div>
          </div>
          {parent_county && (
            <Button data-testid="view-parent-county-btn" onClick={() => navigate(`/county/${parent_county.fips}`)}
              variant="outline" className="bg-white border-emerald-300 text-emerald-800 hover:bg-emerald-50">
              View {parent_county.full_name}
              <ExternalLink className="w-3.5 h-3.5 ml-2" />
            </Button>
          )}
        </div>

        {/* Place stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard label="Type" value={place.lsad_label || place.type} mono />
          <StatCard label="Land area (sq mi)" value={place.area_sq_mi.toFixed(2)} />
          <StatCard label="Latitude" value={place.lat.toFixed(4)} mono />
          <StatCard label="Longitude" value={place.lon.toFixed(4)} mono />
        </div>

        {/* Side-by-side jurisdictions */}
        <section className="mb-10" data-testid="jurisdictions-section">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-3">Permit categories — by jurisdiction</div>
          <div className={`grid gap-5 ${has_city_authority && county_permits ? "lg:grid-cols-2" : "grid-cols-1"}`}>
            {city_permits && (
              <JurisdictionCard
                title={`${place.name} — City permits`}
                subtitle={place.lsad_label || place.type}
                icon={<Building className="w-4 h-4" />}
                accentColor="blue"
                schedule={city_permits}
                categories={city_permits.categories}
                testIdPrefix="city"
              />
            )}
            {county_permits && parent_county && (
              <JurisdictionCard
                title={`${parent_county.full_name} — County permits`}
                subtitle={`FIPS ${parent_county.fips}`}
                icon={<Building2 className="w-4 h-4" />}
                accentColor="emerald"
                schedule={county_permits}
                categories={county_permits.categories}
                testIdPrefix="county"
              />
            )}
          </div>
        </section>

        {/* AHJ playbook — uses new structured visualization */}
        {city_intel && <AhjPlaybook ahjKey={geoid} scope="city" jurisdictionFallback={city_intel.jurisdiction} />}
        {county_intel && parent_county?.fips && (
          <AhjPlaybook ahjKey={parent_county.fips} scope="county" jurisdictionFallback={county_intel.jurisdiction} />
        )}

        <div className="mt-6 rounded-md border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900 leading-relaxed flex gap-3" data-testid="place-disclaimer">
          <Compass className="w-4 h-4 flex-none mt-0.5" />
          <div>
            <strong>Tip:</strong> {has_city_authority
              ? <>For projects in {place.name}, start with the city building department. Some work (e.g. zoning variances, environmental, septic, or unincorporated parcels) may still route through {parent_county?.full_name}.</>
              : <>{place.full_name} doesn&apos;t operate an independent permit office. Submit all building permits to {parent_county?.full_name}.</>}
          </div>
        </div>
      </main>
    </div>
  );
}

function JurisdictionCard({ title, subtitle, icon, accentColor, schedule, categories, testIdPrefix }) {
  const accent = {
    blue: { border: "border-blue-200", bg: "bg-blue-50/40", text: "text-blue-700" },
    emerald: { border: "border-emerald-200", bg: "bg-emerald-50/40", text: "text-emerald-700" },
  }[accentColor];

  return (
    <div className={`rounded-xl border ${accent.border} bg-white p-5`} data-testid={`${testIdPrefix}-jurisdiction-card`}>
      <div className={`${accent.bg} -mx-5 -mt-5 mb-4 px-5 py-3 rounded-t-xl border-b ${accent.border}`}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-md bg-white border ${accent.border} flex items-center justify-center ${accent.text}`}>{icon}</div>
            <div>
              <div className="font-outfit text-base font-semibold text-slate-900">{title}</div>
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">{subtitle}</div>
            </div>
          </div>
          <TierBadge tier={schedule.tier} />
        </div>
        {schedule.schedule_source && schedule.schedule_url && (
          <a href={schedule.schedule_url} target="_blank" rel="noopener noreferrer"
            data-testid={`${testIdPrefix}-source-link`}
            className={`text-[10px] font-mono uppercase tracking-[0.18em] ${accent.text} hover:underline mt-1.5 inline-flex items-center gap-1`}>
            {schedule.schedule_source} <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
      <div className="space-y-2">
        {categories.map((p) => (
          <div key={p.type} className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0" data-testid={`${testIdPrefix}-permit-${p.type.replace(/\s+/g, '-').toLowerCase()}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <FileText className="w-3 h-3 text-slate-400" />
                <span className="text-sm font-medium text-slate-900">{p.type}</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">{p.typical_fee_range} · {p.typical_processing_days} days</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TierBadge({ tier }) {
  if (tier === "curated") return <Badge data-testid="tier-badge" className="bg-emerald-100 text-emerald-800 border-emerald-300 font-mono text-[10px]"><CheckCircle2 className="w-2.5 h-2.5 mr-1" />Curated</Badge>;
  if (tier === "calibrated") return <Badge data-testid="tier-badge" className="bg-amber-100 text-amber-800 border-amber-300 font-mono text-[10px]">Calibrated</Badge>;
  return <Badge data-testid="tier-badge" variant="outline" className="font-mono text-[10px]">Typical</Badge>;
}

function StatCard({ label, value, mono }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-2">{label}</div>
      <div className={`font-outfit text-xl font-bold text-slate-900 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
